import { Transaction, ManagedDocument } from '../types';

export interface TxBalanceInfo {
  totalAmount: number;
  usedAmount: number;
  availableAmount: number;
  allocations: {
    docId: string;
    amount: number;
  }[];
}

export function calculateTransactionBalances(
  transactions: Transaction[],
  documents: ManagedDocument[]
): Record<string, TxBalanceInfo> {
  const result: Record<string, TxBalanceInfo> = {};

  // Initialize transactions
  for (const t of transactions) {
    const totalAmount = t.details.reduce((sum, det) => sum + det.amount, 0);
    result[t.id] = {
      totalAmount,
      usedAmount: 0,
      availableAmount: totalAmount,
      allocations: []
    };
  }

  // Sort documents chronologically by date, then createdAt
  const sortedDocs = [...documents].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    if (dateA !== dateB) return dateA - dateB;
    
    // Fallback to createdAt if applicable
    const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return timeA - timeB;
  });

  // Calculate allocations
  for (const doc of sortedDocs) {
    if (!doc.transactionIds || doc.transactionIds.length === 0) continue;
    
    // We allocate the document's total amount across its linked transactions
    let remainingToAllocate = doc.totalAmount;
    
    // Sort transactions by available amount ascending, 
    // so we fully consume smaller ones and leave the remainder on the largest one.
    const sortedTxIds = [...doc.transactionIds].sort((a, b) => {
      const aAvail = result[a]?.availableAmount ?? 0;
      const bAvail = result[b]?.availableAmount ?? 0;
      return aAvail - bAvail;
    });
    
    for (const tId of sortedTxIds) {
      const tInfo = result[tId];
      if (!tInfo) continue;
      
      const toAllocate = Math.max(0, Math.min(tInfo.availableAmount, remainingToAllocate));
      
      tInfo.usedAmount += toAllocate;
      tInfo.availableAmount -= toAllocate;
      tInfo.allocations.push({ docId: doc.id, amount: toAllocate });
      remainingToAllocate -= toAllocate;
    }
  }

  return result;
}
